import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';
import { saveToCache, getFromCache, CACHE_KEYS } from './cacheService';

let supabase: SupabaseClient;

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
  isPremium?: boolean;
  createdAt?: string;
}

export type AuthState = {
  user: AuthUser | null;
  session: Session | null;
  isLoading: boolean;
};

/**
 * Initialize the Supabase client
 */
export const initializeSupabase = (): SupabaseClient => {
  if (supabase) return supabase;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase environment variables not found');
  }

  supabase = createClient(supabaseUrl, supabaseAnonKey);
  return supabase;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string, returnTo?: string) => {
  const supabase = initializeSupabase();
  
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;
    
    // Cache the user locally for offline access
    if (data.user) {
      saveToCache(CACHE_KEYS.USER, data.user);
    }
    
    // Get additional profile data if available
    if (data.user) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();
      
      // Combine auth data with profile data
      const enhancedUser = {
        ...data.user,
        name: profileData?.full_name || data.user.user_metadata?.full_name,
        isPremium: profileData?.is_premium || false
      };
      
      return { 
        user: enhancedUser, 
        session: data.session,
        returnTo: returnTo
      };
    }
    
    return { user: data.user, session: data.session, returnTo };
  } catch (error) {
    console.error('Sign-in error:', error);
    throw error;
  }
};

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email: string, password: string, metadata?: any) => {
  const supabase = initializeSupabase();
  
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { 
        data: {
          full_name: metadata?.name
        }
      }
    });
    
    if (error) throw error;
    
    // Create a profile record for the new user
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          { 
            id: data.user.id, 
            email: email,
            full_name: metadata?.name || '',
            is_premium: false
          }
        ]);
      
      if (profileError) {
        console.error('Error creating profile:', profileError);
      }
    }
    
    return { user: data.user, session: data.session };
  } catch (error) {
    console.error('Sign-up error:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  const supabase = initializeSupabase();
  
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // Clear the cached user
    localStorage.removeItem(CACHE_KEYS.USER);
    
    return true;
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
};

/**
 * Get the current user and session
 */
export const getCurrentUser = async (): Promise<AuthState> => {
  const supabase = initializeSupabase();
  
  try {
    // First check if we have a session
    const { data } = await supabase.auth.getSession();
    
    if (data?.session) {
      const { data: userData } = await supabase.auth.getUser();
      
      if (userData?.user) {
        // Get additional profile data
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userData.user.id)
          .single();
        
        // Enhanced user with profile data
        const enhancedUser = {
          ...userData.user,
          name: profileData?.full_name || userData.user.user_metadata?.full_name,
          isPremium: profileData?.is_premium || false,
          avatar_url: profileData?.avatar_url || userData.user.user_metadata?.avatar_url
        };
        
        return { 
          user: enhancedUser, 
          session: data.session,
          isLoading: false
        };
      }
      
      return { 
        user: userData?.user || null, 
        session: data.session,
        isLoading: false
      };
    }
    
    // If no online session, try to get from cache for offline functionality
    const cachedUser = getFromCache<AuthUser>(CACHE_KEYS.USER);
    return {
      user: cachedUser,
      session: null,
      isLoading: false
    };
  } catch (error) {
    console.error('Get current user error:', error);
    
    // Fallback to cache if online fetch fails
    const cachedUser = getFromCache<AuthUser>(CACHE_KEYS.USER);
    return {
      user: cachedUser,
      session: null,
      isLoading: false
    };
  }
};

/**
 * Get user profile data with extended profile information
 */
export const getUserProfile = async (userId: string) => {
  const supabase = initializeSupabase();
  
  try {
    // Get profile data from profiles table
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userId: string, updates: Partial<AuthUser>) => {
  const supabase = initializeSupabase();
  
  try {
    // First update the auth metadata if name is being changed
    if (updates.name) {
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { full_name: updates.name }
      });
      
      if (metadataError) {
        console.error('Error updating user metadata:', metadataError);
      }
    }
    
    // Then update the profile record
    const { data, error } = await supabase
      .from('profiles')
      .update({
        full_name: updates.name,
        avatar_url: updates.avatar_url
        // Add other updatable fields as needed
      })
      .eq('id', userId);
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Update profile error:', error);
    throw error;
  }
};

/**
 * Reset password - sends a password reset email
 */
export const resetPassword = async (email: string) => {
  const supabase = initializeSupabase();
  
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

/**
 * Update the user's password
 */
export const updatePassword = async (newPassword: string) => {
  const supabase = initializeSupabase();
  
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Update password error:', error);
    throw error;
  }
};